/**
 * Node assignment and peer tokens: AIOE hands nodes out for shared use
 * (mju-ai-workbench docs/plans/2026-09-agent-nodes.md, "Assignment"). A node
 * is useless until an assignment says who may use it; the node cannot widen
 * its own audience. A person's desktop lists what reaches it and asks for a
 * short-lived peer token per node, which the node verifies against the
 * platform's JWKS.
 *
 *   GET|POST /admin/v1/nodes/:id/assignments, DELETE /admin/v1/nodes/:id/assignments/:assignmentId
 *   GET  /fleet/v1/nodes?for=me          (device token) the nodes that reach this device's person
 *   POST /mesh/v1/peer-token             (device token) { aud: nodeId, scopes[], project? } -> { token, expiresAt, scopes }
 */
import { z } from "zod";
/** What an assignment can let someone use on a node. A closed set. */
export const NODE_CAPABILITIES = ["runtimes", "inference", "dlp", "memory:read", "sandbox", "computer", "hosts:attach"];
export const nodeCapabilitySchema = z.enum(NODE_CAPABILITIES);
/** The manifest role a capability needs; an assignment may only grant what the approved surface offers. */
export const CAPABILITY_ROLE = {
    runtimes: "teams",
    "hosts:attach": "teams",
    "memory:read": "teams",
    inference: "inference",
    dlp: "dlp",
    sandbox: "sandbox",
    computer: "computer",
};
/** The capabilities a node's approved surface can back. `memory:read` also needs memory on offer. */
export function capabilitiesOffered(surface) {
    if (!surface)
        return [];
    return NODE_CAPABILITIES.filter((c) => {
        if (!surface.roles.includes(CAPABILITY_ROLE[c]))
            return false;
        if (c === "memory:read")
            return surface.memory.length > 0;
        if (c === "sandbox")
            return Boolean(surface.sandbox);
        if (c === "computer")
            return Boolean(surface.computer);
        return true;
    });
}
/** Enforced on the node (the only place that knows what is running); AIOE records and reports. */
export const nodeQuotaSchema = z.object({
    maxConcurrentAgents: z.number().int().positive().max(1000).optional(),
    /** Round-robin between principals rather than letting one team fill the node. */
    fairShare: z.boolean().optional(),
    maxConcurrentRequests: z.number().int().positive().max(10_000).optional(),
    tokensPerDay: z.number().int().positive().optional(),
    sandboxes: z.number().int().nonnegative().max(1000).optional(),
    computerSessions: z.number().int().nonnegative().max(1000).optional(),
});
/** Who an assignment reaches: everyone in the tenant, a project's members, an IdP group, or one person. */
export const assignmentScopeSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("tenant") }),
    z.object({ type: z.literal("project"), id: z.string().min(1).max(200) }),
    z.object({ type: z.literal("group"), id: z.string().min(1).max(200) }),
    z.object({ type: z.literal("user"), id: z.string().min(1).max(200) }),
]);
export const nodeAssignmentCreateSchema = z.object({
    scope: assignmentScopeSchema,
    capabilities: z.array(nodeCapabilitySchema).min(1).max(NODE_CAPABILITIES.length),
    quota: nodeQuotaSchema.optional(),
});
export const nodeAssignmentSchema = nodeAssignmentCreateSchema.extend({
    id: z.string(),
    nodeId: z.string(),
    createdBy: z.string(),
    createdAt: z.string(),
});
/** One entry of `GET /fleet/v1/nodes?for=me`: a node that reaches the caller, and what they may use on it. */
export const nodeForMeSchema = z.object({
    nodeId: z.string(),
    name: z.string(),
    tags: z.array(z.string()),
    online: z.boolean(),
    /** The node's overlay address, when it is on the mesh. */
    meshAddress: z.string().optional(),
    /** Overlay ports of its services (control, model, memory, …) from the approved surface. */
    services: z.record(z.string(), z.number().int()),
    runtimes: z.array(z.string()),
    models: z.array(z.string()),
    capabilities: z.array(nodeCapabilitySchema),
    /** The most generous quota among the assignments that reach the caller (the node enforces). */
    quota: nodeQuotaSchema.optional(),
    load: z.object({ runningAgents: z.number().int().nonnegative() }).optional(),
    /**
     * Specialists the organisation has disabled or quarantined on this node (Agent 365 plan, B7), as the
     * node last reported them on its heartbeat: shown greyed with the reason. Absent: none.
     */
    agents: z
        .array(z.object({ specialistId: z.string().min(1).max(200), state: z.enum(["disabled", "quarantined"]), reason: z.string().max(500).optional() }))
        .max(200)
        .optional(),
    /** Why it reaches the caller: `tenant`, `project:<id>`, `group:<id>`, `user:<id>`, `board:<project>`. */
    via: z.array(z.string()),
    /**
     * The enterprise projects whose board of record this node hosts (it holds a canonical root) and
     * the caller is a member of: attach to the node's host for that project.
     */
    boardOf: z.array(z.object({ project: z.string(), rootId: z.string(), role: z.string() })).default([]),
});
export const peerTokenRequestSchema = z.object({
    /** The node's device id. */
    aud: z.string().min(1).max(100),
    scopes: z.array(nodeCapabilitySchema).min(1).max(NODE_CAPABILITIES.length),
    /** Ask within one project: only that project's (and tenant/group/user) assignments count, and the token names it. */
    project: z.string().min(1).max(200).optional(),
});
export const peerTokenResponseSchema = z.object({
    token: z.string(),
    expiresAt: z.string(),
    scopes: z.array(nodeCapabilitySchema),
});
//# sourceMappingURL=assignments.js.map